import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ProjectData {
  id: string;
  name: string;
  description?: string;
  color?: string;
  logo?: string | null;
  sites_count?: number;
  last_modified?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * The project currently being looked at — navigation context, not identity. It lives here rather
 * than in the session because it changes on every navigation and is legitimately absent on
 * `/onboarding`.
 *
 * An absent project is an empty name rather than the literal 'Project Dashboard' it used to
 * carry: consumers could not tell that placeholder from a project actually called that, and it
 * painted on every cold start. Callers render the not-yet-known state instead.
 *
 * It used to mirror itself into five `localStorage` keys, one of them the user's role in the
 * project. The role is `ProjectMembershipService`'s alone now, fetched fresh per project, and the
 * other four went with it: a reload no longer restores the project you were on, which is the
 * accepted cost recorded in `.scratch/session-state/spec.md`. Putting the id in the URL is the
 * better answer and is separate work.
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private projectSubject = new BehaviorSubject<ProjectData>({
    id: '',
    name: '',
    description: '',
    color: '',
    logo: '',
    sites_count: 0
  });

  public project$ = this.projectSubject.asObservable();

  get projectId(): string {
    return this.projectSubject.value.id;
  }

  get projectName(): string {
    return this.projectSubject.value.name;
  }

  get projectColor(): string | null {
    return this.projectSubject.value.color || null;
  }

  get projectLogo(): string | null {
    return this.projectSubject.value.logo || null;
  }

  get projectDescription(): string {
    return this.projectSubject.value.description || '';
  }

  public updateProject(data: Partial<ProjectData>): void {
    const current = this.projectSubject.value;
    const updated = {
      ...current,
      ...data
    };

    // Only emit if something actually changed to avoid infinite loops
    const hasChanged = Object.keys(data).some(key => {
      const currentValue = current[key as keyof ProjectData];
      const newValue = updated[key as keyof ProjectData];
      return currentValue !== newValue;
    });

    if (hasChanged) {
      this.projectSubject.next(updated);
    }
  }

  public setProject(project: ProjectData): void {
    this.updateProject(project);
  }

  public clearProject(): void {
    this.projectSubject.next({
      id: '',
      name: '',
      description: '',
      color: '',
      logo: '',
      sites_count: 0
    });
  }
}
